import React, { useEffect, useRef, useState } from 'react';
import { capacityText } from '../lib/capacity';
import axios from 'axios';
import { ArrowUp, CalendarDays, Clock3, MapPin, MessageCircle, RotateCcw, Users, Sparkles, Check, ArrowRight, LogOut } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { readWorkspace, writeWorkspace } from '../lib/workspaceStorage';
import SpeechInput from '../components/SpeechInput';
import Logo from '../components/Logo';
import './IndividualHome.css';

const API = `${(process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '')}/api`;
const EMPTY = { kind: null, district: null, party_size: null, event_date: null, event_time: null };
const KINDS = [
  ['birthday', 'Doğum günü'],
  ['gathering', 'Arkadaş buluşması'],
  ['company_meeting', 'Şirket toplantısı'],
  ['celebration', 'Özel kutlama'],
  ['other', 'Diğer'],
];
const STARTERS = [
  ['Doğum günü', 'Bir doğum günü için mekân arıyorum.'],
  ['Arkadaş buluşması', 'Arkadaşlarımla buluşmak için mekân bulalım.'],
  ['Şirket toplantısı', 'Ekibimiz için şirket toplantısı planlıyorum.'],
];
const greeting = 'Merhaba! Ben Meko. Nasıl bir buluşma planladığını anlat; birlikte doğru mekânı bulalım. Sesle de anlatabilirsin.';
const cleanError = error => {
  const detail = error?.response?.data?.detail;
  if (typeof detail === 'string') return detail;
  if (detail?.message) return detail.message;
  if (!error?.response) return 'Bağlantı kurulamadı. Yazdıkların korundu, tekrar deneyebilirsin.';
  return 'İşlem tamamlanamadı. Tekrar dene.';
};
const formatDate = date => date ? new Date(`${date}T12:00:00`).toLocaleDateString('tr-TR', { day: 'numeric', month: 'long', year: 'numeric' }) : 'Tarih belirtilmedi';
const imageUrl = venue => {
  const first = venue?.images?.[0];
  const url = typeof first === 'string' ? first : first?.url;
  if (!url || typeof url !== 'string') return null;
  return /^(https?:\/\/|\/)/i.test(url) ? url : null;
};
const previewTime = time => time || 'Saat mekânla netleşecek';

export default function IndividualHome() {
  const { user, logout } = useAuth();
  const storageKey = `individual-meko:${user?.id || user?._id || 'anonymous'}`;
  const saved = useRef(readWorkspace(storageKey, null));
  const [context, setContext] = useState(() => saved.current?.context || EMPTY);
  const [messages, setMessages] = useState(() => saved.current?.messages?.length ? saved.current.messages.slice(-16) : [{ role: 'meko', text: greeting }]);
  const [text, setText] = useState('');
  const [venues, setVenues] = useState(() => saved.current?.venues || []);
  // A7: sunucu en uygun 3 adayı gönderir; toplam uygun aday sayısı ayrıca gelir.
  const [total, setTotal] = useState(() => saved.current?.total || 0);
  const [requests, setRequests] = useState([]);
  const [review, setReview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [audioBusy, setAudioBusy] = useState(false);
  const [sendingRequest, setSendingRequest] = useState(false);
  const [error, setError] = useState('');
  const [requestError, setRequestError] = useState('');
  const requestId = useRef(null);
  const inputRef = useRef(null);
  const conversationEnd = useRef(null);

  useEffect(() => {
    writeWorkspace(storageKey, { context, messages: messages.slice(-16), venues, total });
  }, [storageKey, context, messages, venues, total]);

  useEffect(() => {
    let alive = true;
    axios.get(`${API}/individual/requests`, { timeout: 15000 })
      .then(({ data }) => { if (alive) setRequests(Array.isArray(data) ? data : []); })
      .catch(() => { /* Requests remain available through refresh; chat still works. */ });
    return () => { alive = false; };
  }, []);

  const sendText = async value => {
    const message = String(value || '').trim();
    if (!message || busy || sendingRequest || audioBusy) return;
    setText(''); setError(''); setReview(null); requestId.current = null;
    setMessages(old => [...old, { role: 'user', text: message }].slice(-16));
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/individual/meko`, { message, context }, { timeout: 35000 });
      if (!data || typeof data.reply !== 'string' || !data.context || !Array.isArray(data.venues)) throw new Error('Invalid response');
      setContext(data.context);
      setVenues(data.venues);
      setTotal(Number.isInteger(data.total) ? data.total : data.venues.length);
      setMessages(old => [...old, { role: 'meko', text: data.reply }].slice(-16));
    } catch (e) {
      setError(cleanError(e));
      setText(old => old || message);
    } finally {
      setBusy(false);
      inputRef.current?.focus({ preventScroll: true });
    }
  };

  const startAgain = () => {
    if (busy || sendingRequest || audioBusy) return;
    setMessages([{ role: 'meko', text: greeting }]);
    setContext(EMPTY); setVenues([]); setTotal(0); setReview(null); setText(''); setError(''); setRequestError('');
    requestId.current = null;
    inputRef.current?.focus({ preventScroll: true });
  };

  const chooseVenue = venue => {
    requestId.current = null; setRequestError('');
    setReview({
      venue, kind: context.kind || '', party_size: context.party_size || '',
      event_date: context.event_date || '', event_time: context.event_time || '',
      // The original request belongs to the user and is editable before sharing.
      note: messages.filter(item => item.role === 'user').map(item => item.text).join(' · ').slice(0, 1000),
    });
    setTimeout(() => document.getElementById('individual-request-title')?.scrollIntoView?.({ behavior: 'smooth', block: 'start' }), 0);
  };

  const updateReview = (name, value) => {
    requestId.current = null;
    setReview(old => ({ ...old, [name]: value }));
  };

  const sendRequest = async e => {
    e.preventDefault();
    if (!review || busy || sendingRequest || audioBusy) return;
    setSendingRequest(true); setRequestError('');
    if (!requestId.current) requestId.current = crypto.randomUUID();
    try {
      const payload = {
        venue_id: review.venue.id,
        kind: review.kind,
        party_size: Number(review.party_size),
        event_date: review.event_date,
        event_time: review.event_time || null,
        note: review.note || '',
        request_id: requestId.current,
      };
      const { data } = await axios.post(`${API}/individual/requests`, payload, { timeout: 20000 });
      setRequests(old => [data, ...old.filter(r => r.id !== data.id)].slice(0, 100));
      setMessages(old => [...old, { role: 'meko', text: `${review.venue.name} için talebini ilettim. Mekânın yanıtını Taleplerim bölümünden takip edebilirsin. Henüz kesin rezervasyon yapılmadı.` }].slice(-16));
      setReview(null); requestId.current = null; setError('');
    } catch (err) {
      if (err?.response?.status && err.response.status < 500) requestId.current = null;
      setRequestError(cleanError(err));
    } finally {
      setSendingRequest(false);
    }
  };

  const refreshRequests = async () => {
    try {
      const { data } = await axios.get(`${API}/individual/requests`, { timeout: 15000 });
      setRequests(Array.isArray(data) ? data : []);
      setRequestError('');
    } catch (err) {
      setRequestError(cleanError(err));
    }
  };

  const dateMin = new Date().toLocaleDateString('sv-SE', { timeZone: 'Europe/Istanbul' });
  const hasSearch = Boolean(context.district && context.party_size);
  return (
    <main className="individual-meko" data-testid="individual-home">
      <div className="individual-meko-shell">
        <header className="individual-meko-header">
          <Logo size="sm" />
          <div className="individual-meko-header-actions">
            <span className="individual-meko-user">Bireysel kullanım</span>
            <button type="button" className="individual-meko-exit" onClick={logout}><LogOut size={16} aria-hidden="true" />Çıkış</button>
          </div>
        </header>
        <section className="individual-meko-hero" aria-labelledby="individual-meko-heading">
          <div className="individual-meko-intro">
            <span className="individual-meko-eyebrow"><Sparkles size={15} aria-hidden="true" />MEKO · KİŞİSEL ASİSTANIN</span>
            <h1 id="individual-meko-heading">Bir planın mı var? <span>Meko'ya anlat.</span></h1>
            <p>Doğum günü, arkadaş buluşması ya da şirket toplantısı… İhtiyacını yaz veya sesle söyle. Gerisini birlikte netleştirelim.</p>
          </div>
          <div className="individual-meko-portrait">
            <img src="/brand/meko-cat-original-28ba77df.jpg" alt="Meko, MekandaPro'nun kedi asistanı" width="170" height="170" />
          </div>
        </section>
        <section className="individual-meko-panel" aria-label="Meko ile planlama sohbeti">
          <div className="individual-meko-panel-heading">
            <span><MessageCircle size={17} aria-hidden="true" /> Meko ile konuş</span>
            <button type="button" className="individual-meko-reset" onClick={startAgain} disabled={busy || sendingRequest || audioBusy}>
              <RotateCcw size={15} aria-hidden="true" /> Yeni plan
            </button>
          </div>
          <div className="individual-meko-messages" aria-live="polite" aria-relevant="additions text">
            {messages.map((item, index) => <div key={index} className={`individual-meko-message ${item.role === 'meko' ? 'is-meko' : 'is-user'}`}>
              {item.role === 'meko' && <span className="individual-meko-speaker">Meko</span>}
              <p>{item.text}</p>
            </div>)}
            {busy && <p className="individual-meko-thinking" role="status">Meko isteğini değerlendiriyor…</p>}
            <div ref={conversationEnd} />
          </div>
          {messages.length === 1 && <div className="individual-meko-chips">
            {STARTERS.map(([label, example]) => <button type="button" key={label} onClick={() => sendText(example)} disabled={busy || audioBusy}>{label}<ArrowRight size={14} aria-hidden="true" /></button>)}
          </div>}
          <form onSubmit={e => { e.preventDefault(); sendText(text); }} className="individual-meko-composer" data-testid="individual-meko-composer">
            <label className="individual-meko-sr" htmlFor="individual-meko-input">Meko'ya ne planladığını yaz</label>
            <textarea id="individual-meko-input" ref={inputRef} rows={2} maxLength={1000} value={text}
              onChange={e => setText(e.target.value)} placeholder="Örn. Kadıköy'de 20 kişilik doğum günü için mekân arıyorum."
              onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) { e.preventDefault(); sendText(text); } }}
              disabled={busy || sendingRequest} />
            <div className="individual-meko-composer-actions">
              <SpeechInput onTranscript={transcript => { setText(old => `${old} ${transcript}`.trim().slice(0, 1000)); inputRef.current?.focus(); }}
                onBusyChange={setAudioBusy} disabled={busy || sendingRequest} />
              <button type="submit" className="individual-meko-send" disabled={busy || audioBusy || sendingRequest || !text.trim()}>
                <span>Meko'ya anlat</span><ArrowUp size={19} aria-hidden="true" />
              </button>
            </div>
          </form>
          <p className="individual-meko-footnote">Meko müsaitlik veya kesin rezervasyon garantisi vermez. Talep yalnızca sen onayladığında gönderilir.</p>
          {error && <p role="alert" className="individual-meko-error">{error}</p>}
        </section>
        {hasSearch && <section className="individual-meko-results" aria-label="Mekân adayları" data-testid="individual-meko-venues">
          <div className="individual-meko-section-title">
            <div><span className="individual-meko-eyebrow">MEKO'DAN ÖNERİLER</span><h2>{context.district} için mekân adayları</h2></div>
            <span className="individual-meko-count" data-testid="individual-meko-count">{total > venues.length ? `${total} uygun · en uygun ${venues.length}` : `${venues.length} sonuç`}</span>
          </div>
          {!venues.length && <p className="individual-meko-empty">Bu koşullarda henüz görünür Pro mekân bulamadım. Meko'ya farklı bir ilçe veya kişi sayısı söyleyebilirsin.</p>}
          <div className="individual-meko-venue-grid">
            {venues.map(v => {
              const image = imageUrl(v);
              return <article key={v.id} className="individual-meko-venue">
                <div className="individual-meko-venue-visual">
                  {image ? <img src={image} alt={v.name} loading="lazy" /> : <MapPin size={25} aria-hidden="true" />}
                </div>
                <div className="individual-meko-venue-body">
                  <h3>{v.name}</h3>
                  <p className="individual-meko-meta"><MapPin size={14} aria-hidden="true" />{v.semt} <Users size={14} aria-hidden="true" />{capacityText(v.min_capacity, v.max_capacity)}</p>
                  {v.description && <p className="individual-meko-venue-description">{v.description}</p>}
                  <button type="button" className="individual-meko-venue-action" onClick={() => chooseVenue(v)}>
                    Talep hazırla <ArrowRight size={16} aria-hidden="true" />
                  </button>
                </div>
              </article>;
            })}
          </div>
        </section>}
        {review && <section className="individual-meko-review" aria-labelledby="individual-request-title" data-testid="individual-request-review">
          <span className="individual-meko-eyebrow">SON ADIM · SEN ONAYLAMADAN GÖNDERİLMEZ</span>
          <h2 id="individual-request-title">Talebini gözden geçir</h2>
          <p className="individual-meko-review-venue">{review.venue.name} · {review.venue.semt}</p>
          <form onSubmit={sendRequest} className="individual-meko-review-form">
            <label>Ne planlıyorsun?
              <select value={review.kind} required onChange={e => updateReview('kind', e.target.value)}>
                <option value="">Seç</option>
                {KINDS.map(([value, label]) => <option value={value} key={value}>{label}</option>)}
              </select>
            </label>
            <label>Kişi sayısı
              <input type="number" min={1} max={500} required value={review.party_size} onChange={e => updateReview('party_size', e.target.value)} />
            </label>
            <label><CalendarDays size={15} aria-hidden="true" /> Tarih
              <input type="date" min={dateMin} required value={review.event_date} onChange={e => updateReview('event_date', e.target.value)} />
            </label>
            <label><Clock3 size={15} aria-hidden="true" /> Saat (isteğe bağlı)
              <input type="time" value={review.event_time} onChange={e => updateReview('event_time', e.target.value)} />
            </label>
            <label className="individual-meko-wide">Mekâna iletilecek açıklaman (göndermeden önce düzenleyebilirsin)
              <textarea rows={2} maxLength={1000} value={review.note} onChange={e => updateReview('note', e.target.value)}
                placeholder="Özel bir isteğin veya eklemek istediğin detay var mı?" />
            </label>
            <div className="individual-meko-review-footer">
              <p>Bu bir rezervasyon talebidir. Müsaitlik ve koşullar mekânla teyit edilir.</p>
              <div className="individual-meko-review-buttons">
                <button type="button" className="individual-meko-reset" disabled={sendingRequest} onClick={() => { setReview(null); requestId.current = null; }}>Vazgeç</button>
                <button type="submit" className="individual-meko-send" disabled={sendingRequest || busy}>
                  {sendingRequest ? 'Gönderiliyor…' : 'Talebi onayla ve gönder'} <Check size={17} aria-hidden="true" />
                </button>
              </div>
            </div>
          </form>
          {requestError && <p role="alert" className="individual-meko-error">{requestError}</p>}
        </section>}
        <section className="individual-meko-requests">
          <div className="individual-meko-section-title"><div><span className="individual-meko-eyebrow">TAKİP</span><h2>Taleplerim</h2></div>
            <button type="button" className="individual-meko-reset" onClick={refreshRequests}>Yenile</button>
          </div>
          {requests.length === 0 ? <p className="individual-meko-empty">Henüz talep göndermedin. Meko'yla planını oluşturmaya başlayabilirsin.</p> :
            <div className="individual-meko-request-list">{requests.map(request => <article key={request.id}>
              <div><strong>{request.venue_name}</strong><p>{formatDate(request.event_date)} · {request.party_size} kişi · {previewTime(request.event_time)}</p></div>
              <span className="individual-meko-request-status">{request.status === 'pending' ? 'Yanıt bekleniyor' : request.status === 'accepted' ? 'Görüşmeye uygun' : 'Reddedildi'}</span>
            </article>)}</div>}
          {requestError && !review && <p role="alert" className="individual-meko-error">{requestError}</p>}
        </section>
      </div>
    </main>
  );
}
